import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { MonthlyTrendPoint } from '../../hooks/useKPIs'

interface RevenueOpsChartProps {
  data: MonthlyTrendPoint[]
}

// "2025-01" → "Gen '25"
function formatMonth(month: string): string {
  const [year, m] = month.split('-')
  const MONTHS = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic']
  const label = MONTHS[parseInt(m, 10) - 1] ?? m
  return `${label} '${year.slice(2)}`
}

function formatEurK(value: number): string {
  if (value >= 1_000_000) return `€${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `€${(value / 1_000).toFixed(0)}k`
  return `€${value.toFixed(0)}`
}

function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: Array<{ name: string; value: number; color: string }>
  label?: string
}) {
  if (!active || !payload?.length) return null

  return (
    <div className="rounded-xl border border-[--km-border] bg-[#141b28] px-4 py-3 shadow-xl text-sm">
      <p className="text-[--km-text-secondary] mb-2 font-medium">{label}</p>
      {payload.map((entry) => (
        <div key={entry.name} className="flex items-center gap-2">
          <span className="inline-block h-2 w-2 rounded-sm" style={{ background: entry.color }} />
          <span className="text-[--km-text-secondary]">{entry.name}:</span>
          <span className="text-[--km-text-primary] font-semibold tabular-nums">
            {formatEurK(entry.value)}
          </span>
        </div>
      ))}
    </div>
  )
}

function EmptyState() {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 py-12">
      <p className="text-sm text-[--km-text-muted]">Nessun dato di ordinato disponibile.</p>
      <p className="text-xs text-[--km-text-muted]">Carica gli ordini per visualizzare il trend.</p>
    </div>
  )
}

function CustomLegend() {
  return (
    <div className="flex items-center justify-end gap-4 text-xs text-[--km-text-muted] pb-1">
      <span className="flex items-center gap-1.5">
        <span className="inline-block h-2.5 w-2.5 rounded-sm bg-[#6366f1]" />
        Ordinato
      </span>
      <span className="flex items-center gap-1.5">
        <span className="inline-block h-2.5 w-2.5 rounded-sm bg-[#a855f7]" />
        Competenziato
      </span>
    </div>
  )
}

export function RevenueOpsChart({ data }: RevenueOpsChartProps) {
  if (!data.length) return <EmptyState />

  const chartData = data.map((d) => ({
    ...d,
    mese: formatMonth(d.month),
  }))

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={chartData} margin={{ top: 4, right: 16, bottom: 0, left: 0 }} barGap={3}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />

        <XAxis
          dataKey="mese"
          tick={{ fill: 'var(--km-text-muted)', fontSize: 11 }}
          axisLine={false}
          tickLine={false}
          dy={8}
          interval="preserveStartEnd"
        />

        <YAxis
          tickFormatter={formatEurK}
          tick={{ fill: 'var(--km-text-muted)', fontSize: 11 }}
          axisLine={false}
          tickLine={false}
          width={44}
        />

        <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />

        <Legend content={<CustomLegend />} />

        <Bar
          dataKey="confirmed"
          name="Ordinato"
          fill="#6366f1"
          radius={[3, 3, 0, 0]}
          maxBarSize={28}
          fillOpacity={0.9}
        />
        <Bar
          dataKey="recognized"
          name="Competenziato"
          fill="#a855f7"
          radius={[3, 3, 0, 0]}
          maxBarSize={28}
          fillOpacity={0.75}
        />
      </BarChart>
    </ResponsiveContainer>
  )
}
